// WbPriceBandsPage — /weighbridge/pricing/bands.
//
// Phase C4 Group 1. Lists the tenant's price bands from
// /api/v1/wb/price-bands. The list endpoint is non-paginated server-side
// (the practical row count is dozens, not thousands). Row click navigates
// to the detail/edit page; "+ Add Price Band" routes to /new.
//
// The General Public band is the seeded fallback used by the resolver
// chain when no contract / customer-setting / explicit band applies. It
// is undeletable — the row's Delete button is rendered disabled and
// wrapped in a Tooltip explaining why.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Tag, Trash2 } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { Tooltip } from '../../../components/ui/Tooltip/Tooltip';
import {
  useDeletePriceBand,
  useWbPriceBands,
  type CalcOp,
  type WbPriceBand,
} from '../hooks/useWbPriceBands';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbPriceBandsPage.module.css';

const GBP_NUM = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Render the calc formula as a short, readable string. Uses U+2212 for
 *  minus so the column doesn't read like a hyphen. */
function formatFormula(op: CalcOp | null, value: number | null): string {
  if (op === null || value === null || !Number.isFinite(value)) return '—';
  switch (op) {
    case 'plus_amount':
      return `+ £${GBP_NUM.format(value)}/unit`;
    case 'minus_amount':
      return `− £${GBP_NUM.format(value)}/unit`;
    case 'plus_percent':
      return `+ ${value}%`;
    case 'minus_percent':
      return `− ${value}%`;
  }
}

export function WbPriceBandsPage(): JSX.Element {
  const navigate = useNavigate();
  const bandsQuery = useWbPriceBands();
  const bands = bandsQuery.data?.price_bands ?? [];

  // Delete confirmation — we hold the candidate band in local state so the
  // ConfirmModal can render its name. The delete hook is keyed by enc_id
  // so we instantiate it lazily via the candidate.
  const [confirmTarget, setConfirmTarget] = useState<WbPriceBand | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteMut = useDeletePriceBand(confirmTarget?.enc_id ?? '');

  function handleConfirmDelete() {
    if (!confirmTarget) return;
    deleteMut.mutate(undefined, {
      onSuccess: () => {
        setConfirmTarget(null);
      },
      onError: (err) => {
        setDeleteError(extractErrorMessage(err));
        setConfirmTarget(null);
      },
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Price Bands"
        subtitle="Customer-facing pricing — fixed per product or calculated from the catalogue default."
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/weighbridge/pricing/bands/new')}
          >
            Add Price Band
          </Button>
        }
      />

      {bandsQuery.isError ? (
        <p className={styles.error}>{extractErrorMessage(bandsQuery.error)}</p>
      ) : null}
      {deleteError ? <p className={styles.error}>{deleteError}</p> : null}

      <Card flush>
        {bandsQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading price bands" />
          </div>
        ) : bands.length === 0 ? (
          <EmptyState
            icon={Tag}
            title="No price bands yet"
            description="Add a price band to start applying tiered pricing to customers."
            action={
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => navigate('/weighbridge/pricing/bands/new')}
              >
                Add Price Band
              </Button>
            }
          />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Type</th>
                <th>Lines / Formula</th>
                <th>Default?</th>
                <th>General Public?</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {bands.map((b) => (
                <tr
                  key={b.enc_id}
                  onClick={() =>
                    navigate(
                      `/weighbridge/pricing/bands/${encodeURIComponent(b.enc_id)}`,
                    )
                  }
                >
                  <td>{b.name}</td>
                  <td className={styles.codeCell}>{b.code}</td>
                  <td>
                    {b.type === 'calculated' ? (
                      <Badge variant="info">Calculated</Badge>
                    ) : (
                      <Badge variant="muted">Manual</Badge>
                    )}
                  </td>
                  <td className={b.type === 'calculated' ? styles.formula : undefined}>
                    {b.type === 'calculated'
                      ? formatFormula(b.calc_op, b.calc_value)
                      : `${b.line_count} product${b.line_count === 1 ? '' : 's'}`}
                  </td>
                  <td>
                    {b.is_default_for_new_customers ? (
                      <Badge variant="success">Yes</Badge>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                  <td>
                    {b.is_general_public ? (
                      <Badge variant="info">Yes</Badge>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                  <td className={styles.actionsCell}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/weighbridge/pricing/bands/${encodeURIComponent(b.enc_id)}`,
                        );
                      }}
                    >
                      Edit
                    </Button>
                    {b.is_general_public ? (
                      <Tooltip content="General Public band cannot be deleted">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Trash2}
                          disabled
                          aria-disabled="true"
                          aria-label="Delete (disabled)"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Trash2}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteError(null);
                          setConfirmTarget(b);
                        }}
                      >
                        Delete
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <ConfirmModal
        open={confirmTarget !== null}
        title="Delete price band?"
        message={
          <p>
            Delete <strong>{confirmTarget?.name ?? 'this band'}</strong>? This
            cannot be undone.
          </p>
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTarget(null)}
        loading={deleteMut.isPending}
      />
    </div>
  );
}
